import React, { useEffect, useMemo, useRef, useState } from 'react'

import './tracking.css'
import PercentageCard from '../../components/percentageCard';
import { MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { timeToSeconds, calculateTimeDifference, findClosestCheckpoint, findClosestTurn, formatTime, findClosestCheckpointOptimized, findClosestCheckpointOptimized2, getAngleDifference, isDirectionalRoutePoint } from '../../utils/generalFunctions';
import Footer from '../footer'; 
import BootstrapTooltip from '../../components/tooltip';

const Tracking = ({ devices, deviceOrientations, userSchedules, userEvents, userRoute, locations, toleranceTime, compensationMode, compensationTime, updateDevicesStatusColor }) => {

    const [lastUpdate, setLastUpdate] = useState('-');

    const [rows, setRows] = useState([]);

    const [isFullScreen, setIsFullScreen] = useState(false);

    const [vehicleFilter, setVehicleFilter] = useState('');
    const [checkpointFilter, setCheckpointFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [order, setOrder] = useState('init'); // Orden actual: 'init', 'asc', 'desc'
    const [orderBy, setOrderBy] = useState(null); // Columna para ordenar

    const [statusOrderState, setStatusOrderState] = useState('default');

    const [statusCounts, setStatusCounts] = useState({
        atrasados: 0,
        enTiempo: 0,
        adelantados: 0,
    });

    const [pinnedVehicles, setPinnedVehicles] = useState(new Set());

    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);

    const [delayCounters, setDelayCounters] = useState({});

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const prevRowsRef = useRef(rows);

    useEffect(() => {
        const prevRows = prevRowsRef.current;

        const changed = prevRows.length !== rows.length || prevRows.some((row, index) => {
            const current = rows[index];

            return (row.id !== current.id || row.status !== current.status || row.delay !== current.delay);
        });

        if (changed) {
            setLastUpdate(new Date().toLocaleTimeString());
            prevRowsRef.current = rows;
        }
    }, [rows]);

    const normalizeLabel = (label) => {
        return label.replace(' Ida', '').replace(' Vuelta', '');
    }

    const normalizeTimeToSeconds = (time) => {
        if (time == null || time === ' - ') return null;
        return typeof time === 'string' ? timeToSeconds(time) : time;
    };

    const getCompensationSeconds = (value) => {
        const rawTime = typeof value === 'string' ? value : value?.time;

        if (!rawTime) return 0;

        const [minutes = 0, seconds = 0] = rawTime.split(':').map(Number);

        return ((minutes || 0) * 60) + (seconds || 0);
    };

    const normalizeCompensatedSeconds = (seconds) => {
        const daySeconds = 86400;

        return ((seconds % daySeconds) + daySeconds) % daySeconds;
    };

    const compensationOffsetSeconds = useMemo(() => {
        if (!compensationMode) return 0;

        const seconds = getCompensationSeconds(compensationTime);
        const type = typeof compensationTime === 'string' ? 'late' : compensationTime?.type;

        return type === 'ahead' ? seconds : -seconds;
    }, [compensationMode, compensationTime]);

    const compensatedSchedules = useMemo(() => {
        if (!compensationOffsetSeconds) return userSchedules;

        return userSchedules.map(schedule => ({
            ...schedule,
            turns: schedule.turns?.map(turn =>
                turn.map(point => ({
                    ...point,
                    time: point.time == null
                        ? point.time
                        : normalizeCompensatedSeconds(point.time + compensationOffsetSeconds)
                }))
            ) || []
        }));
    }, [userSchedules, compensationOffsetSeconds]);

    const getCurrentTimeInSeconds = () => {
        const now = new Date();

        return (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds());
    };

    const getElapsedDelaySeconds = (startTime) => {
        const startSeconds = normalizeTimeToSeconds(startTime);

        if (startSeconds == null) return 0;

        const currentSeconds = getCurrentTimeInSeconds();

        return currentSeconds >= startSeconds ? currentSeconds - startSeconds : currentSeconds + 86400 - startSeconds;
    };

    const getStatusCountsFromRows = (rowsToCount) => {
        return rowsToCount.reduce(
            (counts, row) => {
                if (row.status === 'Atrasado') counts.atrasados += 1;
                if (row.status === 'En tiempo') counts.enTiempo += 1;
                if (row.status === 'Adelantado') counts.adelantados += 1;

                return counts;
            },
            {
                atrasados: 0,
                enTiempo: 0,
                adelantados: 0,
            }
        );
    };


    const orderEventsByTracker = (events) => {
        // console.log(events)
        // Ruta activa
        const activeRoute = userRoute.filter(
            point => point.state === 'activo'
        );

        // console.log(activeRoute)
        const eventsByTracker = {};

        events.forEach(event => {
            if (!eventsByTracker[event.tracker_id]) {
                eventsByTracker[event.tracker_id] = [];
            }

            eventsByTracker[event.tracker_id].push(event);
        });

        const orderedEventsByTracker = {};

        Object.entries(eventsByTracker).forEach(
            ([trackerId, trackerEvents]) => {
                const eventsSortedByTime = [...trackerEvents].sort((a, b) =>
                    a.time.localeCompare(b.time)
                );

                let currentRouteIndex = -1;

                const orderedEvents = [];

                eventsSortedByTime.forEach(event => {
                    const zoneId = event.extra.zone_ids?.[0];

                    const matchingIndexes = activeRoute
                        .map((point, index) => ({
                            index,
                            id: point.id
                        }))
                        .filter(point => point.id === zoneId)
                        .map(point => point.index);

                    if (matchingIndexes.length === 0) {
                        return;
                    }

                    let nextIndex = -1;

                    // PRIMER EVENTO
                    if (currentRouteIndex === -1) {
                        nextIndex = matchingIndexes[0];
                    } else {
                        const candidates = matchingIndexes
                            .map(index => {
                                const wrappedDistance = index >= currentRouteIndex ? index - currentRouteIndex : (activeRoute.length - currentRouteIndex) + index;

                                return { index, wrappedDistance };
                            })
                            .filter(candidate =>
                                candidate.wrappedDistance >= 0
                            )
                            .sort((a, b) =>
                                a.wrappedDistance - b.wrappedDistance
                            );

                        const sameCheckpoint = candidates.find(
                            candidate => candidate.wrappedDistance === 0
                        );

                        if (sameCheckpoint) {
                            const lastOrderedEventIndex = orderedEvents.length - 1;

                            orderedEvents[lastOrderedEventIndex] = {
                                ...event,
                                routeIndex: sameCheckpoint.index
                            };

                            return;
                        }

                        const nextCandidate = candidates.find(candidate => candidate.wrappedDistance > 0 && candidate.wrappedDistance <= 3);

                        if (nextCandidate) {
                            nextIndex = nextCandidate.index;
                        }
                    }

                    if (nextIndex === -1) {
                        return;
                    }

                    currentRouteIndex = nextIndex;

                    orderedEvents.push({
                        ...event,
                        routeIndex: nextIndex
                    });
                });

                orderedEventsByTracker[trackerId] = orderedEvents
                    .sort((a, b) =>
                        b.time.localeCompare(a.time)
                    )
                    .slice(0, 10);
            }
        );

        // console.log(orderedEventsByTracker);

        return orderedEventsByTracker;
    };

    const findClosestCheckpoint2 = (eventSeconds, schedule, zoneLabel, turnNumber, checkpointIndex) => {
        let bestMatch = null;
        let bestDifference = Infinity;
        let searching = false;

        for (let i = turnNumber; i < schedule.turns.length; i++) {

            for (let j = checkpointIndex; j < schedule.turns[i].length; j++) {
                const routePoint = schedule.turns[i][j];

                if (routePoint.time == null) {
                    continue;
                }
                if (routePoint.label.toLowerCase().trim() !== zoneLabel.toLowerCase().trim()) {
                    continue;
                }

                const difference = Math.abs(eventSeconds - routePoint.time);

                if (!bestMatch) {
                    bestMatch = { ...routePoint, checkpointIndex: j, turnNumber: i, difference };
                    bestDifference = difference;
                    searching = true;
                    continue;
                } if (difference < bestDifference) {
                    bestDifference = difference;
                    bestMatch = { ...routePoint, checkpointIndex: j, turnNumber: i, difference };
                    continue;
                } if (searching && difference > bestDifference) {
                    break;
                }
            }
        }

        return bestMatch;
    }

    const processData = () => {
        if (compensatedSchedules.length === 0 || userRoute.length === 0) return [];
        // console.log(userEvents)
        // console.log(locations)
        // console.log(devices)
        // console.log(deviceOrientations)

        const orderedFilteredEvents = orderEventsByTracker(userEvents);

        // console.log(orderedFilteredEvents)
        const activeRoute = userRoute.filter(
            point => point.state === 'activo'
        );

        // console.log(activeRoute)
        const newCheckpointMap = {};

        const processedRows = Object.entries(orderedFilteredEvents).flatMap(([trackerId, trackerEvents]) => {
            const device = devices.find(
                device => device.id === Number(trackerId)
            );

            // Ignorar dispositivos inactivos
            if (!device || device.status !== 'activo') {
                return [];
            }

            return trackerEvents.map(event => {
                const trackerLabel = event.extra.tracker_label; // Nombre de la combi
                const zoneId = event.extra.zone_ids[0]; // Id de la geocerca
                let zoneLabel = event.extra.zone_labels[0]; // Nombre de la geocerca 'Alaska'

                const eventTime = event.time.split(' ')[1]; // "10:04:31"

                const location = locations.find(obj => obj.id === devices.find(device => device.id === event.tracker_id)?.source.id || null);

                const connectionStatus = location?.connectionStatus || ' - ';
                const updated = location?.gps?.updated || ' - ';

                const movementStatus = location?.status || ' - ';
                const movementStatusUpdate = location?.movementStatusUpdate || ' - ';
                const speed = location?.speed || 0;

                const schedule = compensatedSchedules.find(schedule => schedule.vehicleId === event.tracker_id);
                if (!schedule) return null;

                const eventSeconds = timeToSeconds(eventTime);

                // console.log(deviceOrientations)

                const isDirectional = activeRoute.filter(point => {
                    const normalizedRoute = normalizeLabel(point.label);

                    return zoneLabel.toLowerCase().trim() === normalizedRoute.toLowerCase().trim() && isDirectionalRoutePoint(point);
                });

                let closestOrientation = null;
                let smallestOrientationDiff = Infinity;

                if (isDirectional.length > 0) {
                    for (const orientation of deviceOrientations[event.tracker_id] || []) {

                        const diff = Math.abs(
                            orientation.seconds - eventSeconds
                        );

                        if (diff < smallestOrientationDiff) {
                            smallestOrientationDiff = diff;
                            closestOrientation = orientation;
                        }
                    }

                    if (closestOrientation) {
                        let filteredDirectional = null;
                        let smallestAngleDiff = Infinity;

                        isDirectional.forEach(point => {
                            if (point.orientation == null) return;

                            const angleDiff = getAngleDifference(
                                closestOrientation.heading,
                                point.orientation
                            );

                            if (angleDiff < smallestAngleDiff) {
                                smallestAngleDiff = angleDiff;
                                filteredDirectional = point;
                            }
                        });

                        if (filteredDirectional) {
                            zoneLabel = filteredDirectional.label;
                        }
                    }
                }

                let foundCheckpoint = null;

                const lastCheckpointData = newCheckpointMap[trackerId];

                let turnNumber = 0;

                let checkpointIndexNumber = 0;

                const closestCheckpoint = findClosestCheckpoint2(
                    eventSeconds,
                    schedule,
                    zoneLabel,
                    turnNumber,
                    checkpointIndexNumber
                );

                // console.log(lastCheckpointData);
                // console.log(closestCheckpoint);

                // Si no hubo coincidencias en ninguna vuelta
                if (closestCheckpoint === null) {
                    return {
                        vehicle: trackerLabel,
                        checkpoint: zoneLabel,
                        time: eventTime,
                        scheduledTime: null,
                        turnNumber: ' - ',
                        status: ' - ',
                        difference: ' - ',
                        checkpointIndex: null,
                        trackerId: event.tracker_id,
                        eventTimestamp: new Date(event.time),
                        movementStatus,
                        movementStatusUpdate,
                        speed,
                        connectionStatus: connectionStatus === 'active' ? 'En línea' : 'Fuera de línea',
                        updated
                    };
                }

                // console.log(newCheckpointMap)

                if (!newCheckpointMap[trackerId]) {

                    newCheckpointMap[trackerId] = {
                        checkpointIndex: closestCheckpoint.checkpointIndex,
                        turnNumber: closestCheckpoint.turnNumber
                    };
                }

                foundCheckpoint = closestCheckpoint;
                turnNumber = closestCheckpoint.turnNumber;

                const turnTime = foundCheckpoint ? foundCheckpoint.time : 0;

                const { status, difference } = calculateTimeDifference(eventTime, turnTime, toleranceTime);

                // Verificar si el siguiente checkpoint ha sido omitido
                // const turnIndex = turnNumber;
                // let checkpointIndex = foundCheckpoint.checkpointIndex;

                // if (turnIndex >= 0 && checkpointIndex !== null) {

                //     if (schedule && schedule.turns.length > turnIndex) {
                //         let currentTurn = schedule.turns[turnIndex];

                //         if (checkpointIndex + 1 >= currentTurn.length) {
                //             currentTurn = schedule.turns[turnIndex + 1];
                //             checkpointIndex = 0;
                //         }

                //         if (checkpointIndex + 1 < currentTurn.length) {

                //             const nextCheckpoint = currentTurn[checkpointIndex + 1];

                //             if (nextCheckpoint && nextCheckpoint.time) {
                //                 const now = new Date();
                //                 const nowInSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

                //                 if (nowInSeconds > nextCheckpoint.time + toleranceTime) {

                //                     return {
                //                         vehicle: trackerLabel,
                //                         checkpoint: nextCheckpoint.finalLabel || nextCheckpoint.label,
                //                         time: '-',
                //                         scheduledTime: nextCheckpoint.time,
                //                         turnNumber: turnNumber,
                //                         status: 'Atrasado',
                //                         difference: '-',
                //                         checkpointIndex: checkpointIndex + 1,
                //                         trackerId: event.tracker_id,
                //                         eventTimestamp: new Date(event.time),
                //                         connectionStatus: connectionStatus === 'active' ? 'En línea' : 'Fuera de línea',
                //                         updated
                //                     };
                //                 }
                //             }
                //         }

                //     }
                // }

                return {
                    vehicle: trackerLabel,
                    checkpoint: zoneLabel,
                    time: eventTime,
                    scheduledTime: turnTime,
                    turnNumber,
                    status,
                    difference,
                    checkpointIndex: foundCheckpoint.checkpointIndex,
                    trackerId: event.tracker_id,
                    eventTimestamp: new Date(event.time),
                    movementStatus,
                    movementStatusUpdate,
                    speed,
                    connectionStatus: connectionStatus === 'active' ? 'En línea' : 'Fuera de línea',
                    updated
                };
            });
        }).filter(Boolean);
        // setRows(processedRows);

        // console.log(userEvents)
        // console.log(orderedFilteredEvents)
        // console.log(processedRows)
        // Identificar dispositivos que no tienen eventos y agregar filas con valores '-'
        devices.filter(device => device.status === "activo" // Filtrar solo dispositivos activos
        ).forEach(device => {
            if (!processedRows.some(row => row.trackerId === device.id)) {
                const location = locations.find(obj => obj.id === device.source.id || null);

                const connectionStatus = location?.connectionStatus || ' - ';
                const updated = location?.gps?.updated || ' - ';

                const movementStatus = location?.status || ' - ';
                const movementStatusUpdate = location?.movementStatusUpdate || ' - ';
                const speed = location?.speed || 0;

                processedRows.push({
                    vehicle: device.label || ' - ',
                    checkpoint: ' - ',
                    time: ' - ',
                    scheduledTime: null,
                    turnNumber: ' - ',
                    status: ' - ',
                    difference: ' - ',
                    checkpointIndex: null,
                    trackerId: device.id,
                    eventTimestamp: new Date(),
                    movementStatus,
                    movementStatusUpdate,
                    speed,
                    connectionStatus: connectionStatus === 'active' ? 'En línea' : 'Fuera de línea',
                    updated
                });
            }
        });

        const vehicles = [...new Set(processedRows.map(row => row.trackerId))];

        const latestEvents = vehicles.map(trackerId => {
            // Filtra todos los eventos de este vehículo, ordenados por eventTimestamp DESC
            const events = processedRows
                .filter(row => row.trackerId === trackerId)
                .sort((a, b) => b.eventTimestamp - a.eventTimestamp);
            // Busca el primer evento con checkpointIndex válido
            const firstValid = events.find(ev => ev.checkpointIndex !== null);
            // Si hay uno válido, lo toma. Si no, toma el más reciente (aunque checkpointIndex sea null)
            return firstValid || events[0];
        }).filter(Boolean); // Por si algún vehículo no tiene eventos

        const nowInSeconds = getCurrentTimeInSeconds();
        const toleranceSeconds = Number(toleranceTime) || 0;

        const immediateDelayCounters = {};

        // Agrupar por trackerId y conservar el último evento para cada vehículo
        // const latestEvents = Object.values(
        //     processedRows.reduce((acc, row) => {
        //         if (!acc[row.trackerId] || acc[row.trackerId].eventTimestamp < row.eventTimestamp) {
        //             acc[row.trackerId] = row; // Reemplazar si el evento actual es más reciente
        //         }
        //         return acc;
        //     }, {})
        // );

        const updatedLatestEvents = latestEvents.map(row => {
            // Ignorar filas inválidas
            if (row.turnNumber === ' - ' || row.checkpointIndex == null) {
                return row;
            }

            immediateDelayCounters[row.trackerId] = {
                startTime: null,
                missedCheckpoints: 0,
                lastCheckpoint: null
            };

            // Ya está atrasado
            if (row.status === 'Atrasado' && row.difference === '-') {
                immediateDelayCounters[row.trackerId] = {
                    startTime: normalizeTimeToSeconds(row.scheduledTime)
                };

                return row;
            }

            const schedule = compensatedSchedules.find(schedule => schedule.vehicleId === row.trackerId);

            if (!schedule) {
                return row;
            }

            let turnIndex = row.turnNumber;

            let checkpointIndex = row.checkpointIndex + 1;

            let nextCheckpoint = schedule.turns?.[turnIndex]?.[checkpointIndex];

            // Siguiente vuelta
            if (!nextCheckpoint) {
                turnIndex += 1;
                checkpointIndex = 0;

                nextCheckpoint = schedule.turns?.[turnIndex]?.[checkpointIndex];
            }

            // No hay siguiente checkpoint
            if (!nextCheckpoint || nextCheckpoint.time == null) {
                return row;
            }

            // Todavía no vence
            const nextCheckpointSeconds = normalizeTimeToSeconds(nextCheckpoint.time);

            if (nextCheckpointSeconds == null) {
                return row;
            }

            if (nowInSeconds < nextCheckpointSeconds + toleranceSeconds) {
                return row;
            }

            // NUEVO ATRASO DETECTADO
            const previousCounter =
                delayCounters[row.trackerId];

            const missedCheckpoints =
                (previousCounter?.missedCheckpoints || 0) + 1;

            immediateDelayCounters[row.trackerId] = {
                startTime: nextCheckpointSeconds,
                missedCheckpoints,
                lastCheckpoint:
                    nextCheckpoint.finalLabel ||
                    nextCheckpoint.label
            };

            const isOutOfRoute = missedCheckpoints > 3;

            return {
                ...row,
                checkpoint: nextCheckpoint.finalLabel || nextCheckpoint.label,
                scheduledTime: nextCheckpoint.time,
                status: isOutOfRoute ? 'Fuera de ruta' : 'Atrasado',
                difference: '-',
                checkpointIndex,
                turnNumber: turnIndex,
                time: '-'
            };
        });

        const isAsc = order === 'asc';
        const sortedRows = [...updatedLatestEvents].sort((a, b) => {
            const aPinned = pinnedVehicles.has(a.trackerId);
            const bPinned = pinnedVehicles.has(b.trackerId);

            if (aPinned && !bPinned) return -1; // `a` va primero
            if (!aPinned && bPinned) return 1;  // `b` va primero

            const valueA = a['time'];
            const valueB = b['time'];

            if (valueA === undefined || valueA === null) return isAsc ? 1 : -1;
            if (valueB === undefined || valueB === null) return isAsc ? -1 : 1;

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            } else if (typeof valueA === 'number' && typeof valueB === 'number') {
                return isAsc ? valueA - valueB : valueB - valueA;
            }

            return 0;
        });

        // setRows(sortedRows);

        // Obtener el último estatus de cada vehículo
        setStatusCounts(getStatusCountsFromRows(updatedLatestEvents));
        // setLastCheckpointIndexMap(newCheckpointMap);
        setDelayCounters(prev => ({
            ...prev,
            ...immediateDelayCounters
        }));

        return sortedRows;
    };

    const [previousRows, setPreviousRows] = useState([]);
    const [updatedRows, setUpdatedRows] = useState(new Set()); // Almacena IDs de vehículos modificados

    useEffect(() => {

        if (locations.length === 0) return;

        let sortedRows = processData();

        if ((order !== 'init' || statusOrderState !== 'default') && orderBy) {
            const isAsc = order === 'asc';
            sortedRows = [...sortedRows].sort((a, b) => {
                const aPinned = pinnedVehicles.has(a.trackerId);
                const bPinned = pinnedVehicles.has(b.trackerId);

                if (aPinned && !bPinned) return -1; // `a` va primero
                if (!aPinned && bPinned) return 1;  // `b` va primero

                if (orderBy === 'status') {
                    // Definir la prioridad de los estados para ordenamiento
                    let statusPriority = { 'Adelantado': 1, 'En tiempo': 2, 'Atrasado': 3 };

                    if (statusOrderState === 'En tiempo') {
                        statusPriority = { 'Adelantado': 2, 'En tiempo': 1, 'Atrasado': 3 };
                    } else if (statusOrderState === 'Atrasado') {
                        statusPriority = { 'Adelantado': 3, 'En tiempo': 2, 'Atrasado': 1 };
                    }

                    const valueA = statusPriority[a.status] || 4; // Si el estado no está en la lista, se coloca al final
                    const valueB = statusPriority[b.status] || 4;

                    return valueA - valueB;
                } else if (orderBy === 'schedule') {
                    const valueA = userSchedules.find(schedule => schedule.vehicleId === a.trackerId)?.name || 'No asignado';
                    const valueB = userSchedules.find(schedule => schedule.vehicleId === b.trackerId)?.name || 'No asignado';

                    // Extraer números de los valores
                    const numA = parseInt(valueA.match(/\d+/)?.[0] || NaN, 10);
                    const numB = parseInt(valueB.match(/\d+/)?.[0] || NaN, 10);

                    if (isNaN(numA) && isNaN(numB)) {
                        // Orden alfabético si ambos valores no tienen números
                        return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                    } else if (isNaN(numA)) {
                        // Los valores sin número se colocan después
                        return isAsc ? 1 : -1;
                    } else if (isNaN(numB)) {
                        // Los valores sin número se colocan después
                        return isAsc ? -1 : 1;
                    } else {
                        // Orden numérico si ambos valores tienen números
                        return isAsc ? numA - numB : numB - numA;
                    }
                }

                const valueA = a[orderBy];
                const valueB = b[orderBy];

                if (valueA === undefined || valueA === null) return isAsc ? 1 : -1;
                if (valueB === undefined || valueB === null) return isAsc ? -1 : 1;

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                } else if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return isAsc ? valueA - valueB : valueB - valueA;
                }

                return 0;
            });
        }
        // Crear un conjunto temporal para almacenar IDs de filas cambiadas
        const changedRows = new Set();

        if (previousRows.length > 0) {
            sortedRows.forEach((newRow) => {
                const prevRow = previousRows.find(row => row.trackerId === newRow.trackerId);

                if (prevRow) {
                    // Comparar valores clave para detectar cambios
                    if (
                        prevRow.checkpoint !== newRow.checkpoint ||
                        prevRow.time !== newRow.time ||
                        prevRow.turnNumber !== newRow.turnNumber ||
                        prevRow.status !== newRow.status ||
                        prevRow.difference !== newRow.difference ||
                        prevRow.connectionStatus !== newRow.connectionStatus
                    ) {
                        changedRows.add(newRow.trackerId);
                    }
                }
            });
        }
        // Actualizar estados
        setRows(sortedRows);
        setPreviousRows(sortedRows); // Guardamos el nuevo estado para futuras comparaciones
        setUpdatedRows(changedRows); // Guardamos los IDs que cambiaron

        // setRows(sortedRows);

    }, [userEvents, devices, locations.length, pinnedVehicles, userSchedules, compensatedSchedules]);


    const rowsRef = useRef(rows);
    const schedulesRef = useRef(compensatedSchedules);
    const toleranceTimeRef = useRef(toleranceTime);

    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    useEffect(() => {
        schedulesRef.current = compensatedSchedules;
    }, [compensatedSchedules]);

    useEffect(() => {
        toleranceTimeRef.current = toleranceTime;
    }, [toleranceTime]);

    useEffect(() => {
        setDelayCounters({});
    }, [compensationMode, compensationTime]);

    useEffect(() => {

        let interval;

        const executeLogic = () => {

            const nowInSeconds = getCurrentTimeInSeconds();
            const toleranceSeconds = Number(toleranceTimeRef.current) || 0;

            const updatedCounters = {};

            let hasChanges = false;

            const newRows = rowsRef.current.map(row => {

                // Filas inválidas
                if (row.turnNumber === ' - ' || row.checkpointIndex == null) {
                    return row;
                }

                // Ya atrasado
                let currentCheckpointIndex = row.checkpointIndex;
                let currentTurnIndex = row.turnNumber;

                if ((row.status === 'Atrasado' || row.status === 'Fuera de ruta') && row.difference === '-') {

                    const previousCounter = delayCounters[row.trackerId];

                    updatedCounters[row.trackerId] = {
                        startTime: normalizeTimeToSeconds(row.scheduledTime),
                        missedCheckpoints: previousCounter?.missedCheckpoints || 0,
                        lastCheckpoint: previousCounter?.lastCheckpoint || null
                    };

                    currentCheckpointIndex = row.checkpointIndex;
                    currentTurnIndex = row.turnNumber;
                }

                const schedule = schedulesRef.current.find(sch => sch.vehicleId === row.trackerId);

                if (!schedule) { return row; }

                let turnIndex = currentTurnIndex;

                let checkpointIndex = currentCheckpointIndex + 1;

                let nextCheckpoint = schedule.turns?.[turnIndex]?.[checkpointIndex];

                // Siguiente vuelta
                if (!nextCheckpoint) {
                    turnIndex += 1;
                    checkpointIndex = 0;

                    nextCheckpoint = schedule.turns?.[turnIndex]?.[checkpointIndex];
                }

                // No hay más checkpoints
                if (!nextCheckpoint || nextCheckpoint.time == null) {
                    return row;
                }

                // Aún no llega la hora
                const nextCheckpointSeconds = normalizeTimeToSeconds(nextCheckpoint.time);

                if (nextCheckpointSeconds == null) {
                    return row;
                }

                if (nowInSeconds < nextCheckpointSeconds + toleranceSeconds) {
                    return row;
                }

                // Nuevo atraso detectado
                const previousCounter = delayCounters[row.trackerId];

                const currentCheckpointLabel = nextCheckpoint.finalLabel || nextCheckpoint.label;

                const alreadyCounted = previousCounter?.lastCheckpoint === currentCheckpointLabel;

                const missedCheckpoints = alreadyCounted ? previousCounter?.missedCheckpoints || 0 : (previousCounter?.missedCheckpoints || 0) + 1;

                updatedCounters[row.trackerId] = {
                    startTime: nextCheckpointSeconds,
                    missedCheckpoints,
                    lastCheckpoint: nextCheckpoint.finalLabel || nextCheckpoint.label
                };

                const isOutOfRoute = missedCheckpoints >= 3;

                hasChanges = true;

                return {
                    ...row,
                    checkpoint: nextCheckpoint.finalLabel || nextCheckpoint.label,
                    scheduledTime: nextCheckpoint.time,
                    status: isOutOfRoute ? 'Fuera de ruta' : 'Atrasado',
                    difference: '-',
                    checkpointIndex,
                    turnNumber: turnIndex,
                    time: '-'
                };
            });

            // ACTUALIZAR COUNTERS
            if (Object.keys(updatedCounters).length > 0) {

                setDelayCounters(prev => ({
                    ...prev,
                    ...updatedCounters
                }));
            }

            // SOLO actualizar rows si hubo cambios
            if (hasChanges) {
                setRows(newRows);
                setStatusCounts(getStatusCountsFromRows(newRows));
            }
        };

        const startAlignedInterval = () => {
            const now = new Date();
            const next = new Date(now);

            // Próximo xx:xx:30
            next.setSeconds(30);
            next.setMilliseconds(0);

            // Si ya pasó el segundo 30,
            // ir al siguiente minuto
            if (now.getSeconds() >= 30) {
                next.setMinutes(next.getMinutes() + 1);
            }

            const delay = next - now;

            const timeout = setTimeout(() => {
                executeLogic();

                interval = setInterval(() => {
                    executeLogic();
                }, 60000);

            }, delay);

            return timeout;
        };

        const timeout = startAlignedInterval();

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };

    }, []);

    const [clock, setClock] = useState(0);

    useEffect(() => {

        const interval = setInterval(() => {
            setClock(prev => prev + 1);
        }, 1000);

        return () => clearInterval(interval);

    }, []);

    // useEffect(() => {
    //     console.log(delayCounters);
    // }, [delayCounters]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Atrasado':
                return { backgroundColor: 'var(--color-danger)' };
            case 'En tiempo':
                return { backgroundColor: 'var(--color-success)' };
            case 'Adelantado':
                return { backgroundColor: 'var(--color-warning)' };
            default:
                return { backgroundColor: 'var(--color-danger)' };
        }
    };

    const filteredRows = rows.filter(row => {
        return (
            (vehicleFilter === '' || row.vehicle === vehicleFilter) &&
            (checkpointFilter === '' || row.checkpoint === checkpointFilter) &&
            (statusFilter === '' || row.status === statusFilter)
        );
    });

    const handleSort = (column) => {
        if (column === 'status') {
            if (order !== 'init') setOrder('init');
            // Definir el ciclo de ordenamiento en orden
            const statusOrderCycle = ['Adelantado', 'En tiempo', 'Atrasado', 'default'];

            // Avanzar al siguiente estado del ciclo
            const currentIndex = statusOrderCycle.indexOf(statusOrderState);
            const newStatusOrder = statusOrderCycle[(currentIndex + 1) % statusOrderCycle.length];

            setStatusOrderState(newStatusOrder);
            setOrderBy(column);

            // Si el estado es "default", restaurar el orden original
            if (newStatusOrder === 'default') {
                setRows(processData());
                return;
            }

            // Definir la prioridad de los estados para ordenamiento dinámico
            let statusPriority;
            if (newStatusOrder === 'Atrasado') {
                statusPriority = { 'Adelantado': 3, 'En tiempo': 2, 'Atrasado': 1 };
            } else if (newStatusOrder === 'En tiempo') {
                statusPriority = { 'Adelantado': 2, 'En tiempo': 1, 'Atrasado': 3 };
            } else if (newStatusOrder === 'Adelantado') {
                statusPriority = { 'Adelantado': 1, 'En tiempo': 2, 'Atrasado': 3 };
            }
            // Ordenar las filas según el estado seleccionado
            const sortedRows = [...rows].sort((a, b) => {
                const aPinned = pinnedVehicles.has(a.trackerId);
                const bPinned = pinnedVehicles.has(b.trackerId);

                if (aPinned && !bPinned) return -1; // `a` va primero
                if (!aPinned && bPinned) return 1;  // `b` va primero

                const valueA = statusPriority[a.status] || 4; // Si el estado no está en la lista, se coloca al final
                const valueB = statusPriority[b.status] || 4;

                return valueA - valueB; // Orden ascendente según la prioridad
            });

            setRows(sortedRows);
            return;
        }

        // Ordenamiento estándar para otras columnas
        const newOrder = orderBy === column
            ? (order === 'init' ? 'asc' : (order === 'asc' ? 'desc' : 'init'))
            : 'asc';

        setStatusOrderState('default');
        setOrder(newOrder);
        setOrderBy(column);

        if (newOrder === 'init') {
            setRows(processData());
            return;
        }

        const isAsc = newOrder === 'asc';

        if (column === 'schedule') {
            const sortedRows = [...rows].sort((a, b) => {

                const aPinned = pinnedVehicles.has(a.trackerId);
                const bPinned = pinnedVehicles.has(b.trackerId);

                if (aPinned && !bPinned) return -1; // `a` va primero
                if (!aPinned && bPinned) return 1;  // `b` va primero

                const valueA = userSchedules.find(schedule => schedule.vehicleId === a.trackerId)?.name || 'No asignado';
                const valueB = userSchedules.find(schedule => schedule.vehicleId === b.trackerId)?.name || 'No asignado';

                // Extraer números de los valores
                const numA = parseInt(valueA.match(/\d+/)?.[0] || NaN, 10);
                const numB = parseInt(valueB.match(/\d+/)?.[0] || NaN, 10);

                if (isNaN(numA) && isNaN(numB)) {
                    // Orden alfabético si ambos valores no tienen números
                    return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                } else if (isNaN(numA)) {
                    // Los valores sin número se colocan después
                    return isAsc ? 1 : -1;
                } else if (isNaN(numB)) {
                    // Los valores sin número se colocan después
                    return isAsc ? -1 : 1;
                } else {
                    // Orden numérico si ambos valores tienen números
                    return isAsc ? numA - numB : numB - numA;
                }
            });

            setRows(sortedRows);
            return;
        }

        const sortedRows = [...rows].sort((a, b) => {
            const aPinned = pinnedVehicles.has(a.trackerId);
            const bPinned = pinnedVehicles.has(b.trackerId);

            if (aPinned && !bPinned) return -1; // `a` va primero
            if (!aPinned && bPinned) return 1;  // `b` va primero

            const valueA = a[column];
            const valueB = b[column];

            if (valueA === undefined || valueA === null) return isAsc ? 1 : -1;
            if (valueB === undefined || valueB === null) return isAsc ? -1 : 1;

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            } else if (typeof valueA === 'number' && typeof valueB === 'number') {
                return isAsc ? valueA - valueB : valueB - valueA;
            }

            return 0;
        });

        setRows(sortedRows);
    };


    const getSortIcon = (column) => {
        if (column !== 'status') {
            if (orderBy !== column || order === 'init') return <span>-</span>;
            return order === 'asc' ? (
                <span className='material-symbols-rounded'>arrow_upward_alt</span>
            ) : (
                <span className='material-symbols-rounded'>arrow_downward_alt</span>
            );
        }

        // Íconos personalizados para la columna "Estatus"
        const statusIcons = {
            'Atrasado': <span style={{ color: 'var(--color-danger)', fontSize: '0.7rem' }}>⬤</span>, // Círculo rojo
            'En tiempo': <span style={{ color: 'var(--color-success)', fontSize: '0.7rem' }}>⬤</span>, // Círculo verde
            'Adelantado': <span style={{ color: 'var(--color-warning)', fontSize: '0.7rem' }}>⬤</span>, // Círculo amarillo
            'default': <span>-</span>, // Guion si está en el orden original
        };

        return statusIcons[statusOrderState] || <span>-</span>;
    };

    const tableCellHeaderStyle = { textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: '#ffff' };
    const tableCellBodyStyle = { textAlign: 'center', fontSize: '0.8rem', color: 'var(--color-dark)', border: '1px solid var(--color-dark-light)', padding: 0 };

    const halfIndex = Math.ceil(rows.length / 2);
    const firstHalf = rows.slice(0, halfIndex);
    const secondHalf = rows.slice(halfIndex);

    // Función para fijar/desfijar un vehículo
    const togglePinnedVehicle = (trackerId) => {
        setPinnedVehicles(prevPinned => {
            const updatedPinned = new Set(prevPinned);
            if (updatedPinned.has(trackerId)) {
                updatedPinned.delete(trackerId); // Desfijar si ya estaba fijado
            } else {
                updatedPinned.add(trackerId); // Fijar si no estaba fijado
            }
            return updatedPinned;
        });
    };

    function formatSeconds(seconds) {
        if (isNaN(seconds) || seconds < 0) return '+00:00:00';
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        // Siempre mostrar dos dígitos de horas, aunque sean 00
        return (
            '+ ' +
            [hrs, mins, secs].map(v => String(v).padStart(2, '0')).join(':')
        );
    }

    const screenWidht = window.innerWidth < 1024;

    const getResponsiveColSpan = (value) => {
        return screenWidht ? value - 2 : value;
    }

    const renderLastUpdate = () => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem' }}>
            {compensationMode && (
                <BootstrapTooltip title="El modo compensación de horario está activo">
                <span
                    className="material-symbols-rounded"
                    style={{ fontSize: '18px', color: 'var(--color-warning)' }}
                >
                    more_time
                </span>
                </BootstrapTooltip>
            )}
            <span>Última actualización: {lastUpdate}</span>
        </span>
    );

    return (
        <section className={`tracking_container ${isFullScreen ? 'fullscreen' : ''} checks_container`}>
            {isFullScreen && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {renderLastUpdate()}
                    <button onClick={toggleFullScreen} className='fullscreen-close-btn'>
                        Salir de Pantalla Completa
                    </button>
                </div>
            )}
            {/* <div className='checks_container'> */}
            <div className='small_cards_container'>
                <PercentageCard
                    title="Vehículos"
                    total={devices.length}
                    mainValue={devices.length}
                    mainClass="primary"
                    vehiclesStatus={devices}
                />
                <PercentageCard
                    title="Adelantados"
                    total={devices.filter(vehicle => vehicle.status === 'activo').length}
                    mainValue={statusCounts.adelantados}
                    mainClass="warning"
                />
                <PercentageCard
                    title="En tiempo"
                    total={devices.filter(vehicle => vehicle.status === 'activo').length}
                    mainValue={statusCounts.enTiempo}
                    mainClass="success"
                />
                <PercentageCard
                    title="Atrasados"
                    total={devices.filter(vehicle => vehicle.status === 'activo').length}
                    mainValue={statusCounts.atrasados}
                    mainClass="danger"
                />
            </div>
            <div className='checks_records' style={{ flex: '1 1 auto' }}>

                <h2 className='card_title'>Checadas</h2>
                <div className='card_content'>
                    <div>
                        {!isFullScreen && (
                            <div className='floating_checks_container'>
                                {renderLastUpdate()}
                                <button onClick={toggleFullScreen} className='fullscreen-open-btn'>
                                    Pantalla Completa
                                </button>
                                {/* <button onClick={() => setShowMap(!showMap)} className='toggle-map-btn'>
                                    <span className="material-symbols-rounded">{showMap ? 'chevron_right' : 'chevron_left'}</span>
                                </button> */}
                            </div>
                        )}
                        {filteredRows.length === 0 ?
                            <>
                                <h2 style={{ textAlign: 'center', marginTop: '1.5rem' }}> Sin información a mostrar.</h2>
                                <span style={{ textAlign: 'center', display: 'block' }}>Verifique que haya vehículos activos y que los horarios asignados sean correctos.</span>
                            </>
                            : (
                                (!isFullScreen || isSmallScreen) ?
                                    <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', borderRadius: 'var(--card-border-radius)', overflowX: 'hidden' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('vehicle')}><span>Vehículo</span>{getSortIcon('vehicle')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('checkpoint')}><span>Punto de Control</span>{getSortIcon('checkpoint')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('time')}><span>Tiempo</span>{getSortIcon('time')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('turnNumber')}><span>Vuelta</span>{getSortIcon('turnNumber')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('status')}><span>Estatus</span>{getSortIcon('status')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('difference')}><span>Diferencia</span>{getSortIcon('difference')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('schedule')}><span>Horario</span>{getSortIcon('schedule')}</TableCell>
                                                    <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('connectionStatus')}><span>Conexión</span>{getSortIcon('connectionStatus')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    filteredRows//.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                        .map((row, index) => (
                                                            <TableRow
                                                                key={index}
                                                                className={updatedRows.has(row.trackerId) ? 'row-updated' : ''}
                                                                onClick={() => togglePinnedVehicle(row.trackerId)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    backgroundColor: pinnedVehicles.has(row.trackerId) ? 'var(--color-background-hover)' : 'inherit', // Resaltar vehículos fijados
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                            >
                                                                {(userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA') === 'NA' ? (
                                                                    <>
                                                                        <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                        <TableCell sx={tableCellBodyStyle} style={{ fontStyle: 'italic', opacity: '0.8' }} colSpan={getResponsiveColSpan(6)}>
                                                                            No hay un horario asignado a este vehículo.
                                                                        </TableCell>
                                                                        <TableCell
                                                                            sx={tableCellBodyStyle}
                                                                            style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    alignItems: 'center',
                                                                                    gap: '0.1rem',
                                                                                    lineHeight: 1.1
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.35rem',
                                                                                        flexWrap: 'wrap',
                                                                                        justifyContent: 'center'
                                                                                    }}
                                                                                >
                                                                                    <span>{row.connectionStatus}</span>

                                                                                    <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                        {row.movementStatus === 'moving' ? `M ${Math.round(row.speed)} km/h` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                    </span>
                                                                                </div>

                                                                                <span>
                                                                                    {new Date(row.updated).toLocaleTimeString()}
                                                                                </span>
                                                                                
                                                                            </div>
                                                                        </TableCell>
                                                                    </>
                                                                ) : (
                                                                    row.checkpoint === ' - ' ?
                                                                        <>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle} style={{ fontStyle: 'italic', opacity: '0.8' }} colSpan={getResponsiveColSpan(5)}>
                                                                                No se encontraron coincidencias.
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA'}</TableCell>
                                                                            <TableCell
                                                                                sx={tableCellBodyStyle}
                                                                                style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '0.35rem',
                                                                                            flexWrap: 'wrap',
                                                                                            justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span>{row.connectionStatus}</span>
                                                                                        <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                            {row.movementStatus === 'moving' ? `` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                        </span>
                                                                                    </div>

                                                                                    <span>{new Date(row.updated).toLocaleTimeString()}</span>
                                                                                </div>
                                                                            </TableCell>
                                                                        </>
                                                                        :
                                                                        <>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <span>{row.checkpoint}</span>
                                                                                    {
                                                                                        row.scheduledTime != null && (
                                                                                            <span style={{ fontSize: '0.7rem', }}>
                                                                                                {formatTime(row.scheduledTime)}
                                                                                            </span>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.time}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.turnNumber + 1}</TableCell>
                                                                            <TableCell sx={{ textAlign: 'center', border: '1px solid var(--color-dark-light)' }}>
                                                                                <span className={`table_status_value ${row.status === ' - ' ? null : ''}`} style={getStatusStyle(row.status)}>{row.status}</span>
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>
                                                                                {((row.status === 'Atrasado' || row.status === 'Fuera de ruta') && row.difference === '-') ?
                                                                                    <span style={{ color: 'var(--color-danger)' }}>
                                                                                        {(() => {

                                                                                            if (!delayCounters[row.trackerId]) {
                                                                                                return '+00:00:00';
                                                                                            }

                                                                                            return formatSeconds(
                                                                                                getElapsedDelaySeconds(
                                                                                                    delayCounters[row.trackerId].startTime
                                                                                                )
                                                                                            );

                                                                                        })()}
                                                                                    </span>
                                                                                    :
                                                                                    row.difference
                                                                                }
                                                                            </TableCell>

                                                                            <TableCell sx={tableCellBodyStyle}>{userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA'}</TableCell>
                                                                            <TableCell
                                                                                sx={tableCellBodyStyle}
                                                                                style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '0.35rem',
                                                                                            flexWrap: 'wrap',
                                                                                            justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span>{row.connectionStatus}</span>
                                                                                        <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                            {row.movementStatus === 'moving' ? `` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                        </span>
                                                                                    </div>

                                                                                    <span>{new Date(row.updated).toLocaleTimeString()}</span>
                                                                                </div>
                                                                            </TableCell>
                                                                        </>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    :
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        {[firstHalf, secondHalf].map((half, index) => (
                                            <TableContainer key={index} component={Paper} sx={{ backgroundColor: 'transparent', borderRadius: 'var(--card-border-radius)', overflowX: 'hidden' }}>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('vehicle')}><span>Vehículo</span>{getSortIcon('vehicle')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('checkpoint')}><span>Punto de Control</span>{getSortIcon('checkpoint')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('time')}><span>Tiempo</span>{getSortIcon('time')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('turnNumber')}><span>Vuelta</span>{getSortIcon('turnNumber')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('status')}><span>Estatus</span>{getSortIcon('status')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('difference')}><span>Diferencia</span>{getSortIcon('difference')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('schedule')}><span>Horario</span>{getSortIcon('schedule')}</TableCell>
                                                            <TableCell sx={tableCellHeaderStyle} onClick={() => handleSort('connectionStatus')}><span>Conexión</span>{getSortIcon('connectionStatus')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {half.map((row, idx) => (
                                                            <TableRow key={idx} className={updatedRows.has(row.trackerId) ? 'row-updated' : ''}
                                                                onClick={() => togglePinnedVehicle(row.trackerId)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    backgroundColor: pinnedVehicles.has(row.trackerId) ? 'var(--color-background-hover)' : 'inherit', // Resaltar vehículos fijados
                                                                    transition: 'all 0.3s ease'
                                                                }}>
                                                                {(userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA') === 'NA' ? (
                                                                    <>
                                                                        <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                        <TableCell sx={tableCellBodyStyle} style={{ fontStyle: 'italic', opacity: '0.8' }} colSpan={getResponsiveColSpan(6)}>
                                                                            No hay un horario asignado a este vehículo.
                                                                        </TableCell>
                                                                        <TableCell
                                                                                sx={tableCellBodyStyle}
                                                                                style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '0.35rem',
                                                                                            flexWrap: 'wrap',
                                                                                            justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span>{row.connectionStatus}</span>
                                                                                        <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                            {row.movementStatus === 'moving' ? `` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                        </span>
                                                                                    </div>

                                                                                    <span>{new Date(row.updated).toLocaleTimeString()}</span>
                                                                                </div>
                                                                            </TableCell>
                                                                    </>
                                                                ) : (
                                                                    row.checkpoint === ' - ' ?
                                                                        <>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle} style={{ fontStyle: 'italic', opacity: '0.8' }} colSpan={getResponsiveColSpan(5)}>
                                                                                No se encontraron coincidencias.
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA'}</TableCell>
                                                                           <TableCell
                                                                                sx={tableCellBodyStyle}
                                                                                style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '0.35rem',
                                                                                            flexWrap: 'wrap',
                                                                                            justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span>{row.connectionStatus}</span>
                                                                                        <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                            {row.movementStatus === 'moving' ? `` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                        </span>
                                                                                    </div>

                                                                                    <span>{new Date(row.updated).toLocaleTimeString()}</span>
                                                                                </div>
                                                                            </TableCell>
                                                                        </>
                                                                        :
                                                                        <>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <span>{row.checkpoint}</span>

                                                                                    {
                                                                                        row.scheduledTime != null && (
                                                                                            <span
                                                                                                style={{
                                                                                                    fontSize: '0.7rem',
                                                                                                }}
                                                                                            >
                                                                                                {formatTime(row.scheduledTime)}
                                                                                            </span>
                                                                                        )
                                                                                    }
                                                                                </div>
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.time}</TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{row.turnNumber + 1}</TableCell>
                                                                            <TableCell sx={{ textAlign: 'center', border: '1px solid var(--color-dark-light)' }}>
                                                                                <span className={`table_status_value ${row.status === ' - ' ? null : ''}`} style={getStatusStyle(row.status)}>{row.status}</span>
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>
                                                                                {((row.status === 'Atrasado' || row.status === 'Fuera de ruta') && row.difference === '-') ?
                                                                                    <span style={{ color: 'var(--color-danger)' }}>
                                                                                        {(() => {

                                                                                            if (!delayCounters[row.trackerId]) {
                                                                                                return '+00:00:00';
                                                                                            }

                                                                                            return formatSeconds(
                                                                                                getElapsedDelaySeconds(
                                                                                                    delayCounters[row.trackerId].startTime
                                                                                                )
                                                                                            );

                                                                                        })()}
                                                                                    </span>
                                                                                    :
                                                                                    row.difference
                                                                                }
                                                                            </TableCell>
                                                                            <TableCell sx={tableCellBodyStyle}>{userSchedules.find(schedule => schedule.vehicleId === row.trackerId)?.name || 'NA'}</TableCell>
                                                                            <TableCell
                                                                                sx={tableCellBodyStyle}
                                                                                style={{color: row.connectionStatus === 'En línea' ? 'var(--color-success)' : 'var(--color-danger)'}}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        gap: '0.1rem',
                                                                                        lineHeight: 1.1
                                                                                    }}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '0.35rem',
                                                                                            flexWrap: 'wrap',
                                                                                            justifyContent: 'center'
                                                                                        }}
                                                                                    >
                                                                                        <span>{row.connectionStatus}</span>
                                                                                        <span style={{color: row.movementStatus === 'moving' ? 'var(--color-success)' : 'var(--color-primary)'}}>
                                                                                            {row.movementStatus === 'moving' ? `` : row.movementStatus === 'parked' ? `P ${new Date(row.movementStatusUpdate).toLocaleTimeString()}` : ''}
                                                                                        </span>
                                                                                    </div>

                                                                                    <span>{new Date(row.updated).toLocaleTimeString()}</span>
                                                                                </div>
                                                                            </TableCell>
                                                                        </>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ))}
                                    </div>
                            )
                        }
                    </div>
                    {/* <div className={`map_container ${showMap ? '' : 'collapsed'}`}>
                        {locations.length > 0 ?
                            <Map locations={locations} devices={devices} />
                            : ''}
                    </div> */}
                </div>
            </div>
            {/* </div> */}
            {isFullScreen &&
                <Footer isFullScreen={true} />
            }
        </section>
    )
}

export default Tracking
